import { Website } from '@superfunnel/types';
import { useCallback } from 'react';
import { UseLandingPageAgentSessionReturn } from '../../../hooks/useLandingPageAgentSession';
import { UseWebsiteUpdaterReturn } from './useWebsiteUpdater';

export interface UseAgentSessionActionsProps {
  mode: 'globalSection' | 'website';
  dynamicPageId: string;
  agentSession: UseLandingPageAgentSessionReturn;
  websiteUpdater: UseWebsiteUpdaterReturn;
  selectedWebsiteId: string | null;
  website: Website | undefined;
  rawWebsite: Website | undefined;
  sendMessageToIframe: (message: any) => void;
  moveFromDraft: () => void;
}

export interface UseAgentSessionActionsReturn {
  agentAcceptChange: (changeId: string) => Promise<void>;
  agentAcceptAllChanges: () => Promise<void>;
  agentDiscardChange: (changeId: string) => Promise<void>;
  agentDiscardAllChanges: () => Promise<void>;
  agentChangeClick: (changeId: string) => void;
}

/**
 * Hook to handle agent session actions like accepting changes
 * and applying them to the website
 * @return {UseAgentSessionActionsReturn} Object with agent action functions
 */
export function useAgentSessionActions({
  mode,
  dynamicPageId,
  agentSession,
  websiteUpdater,
  selectedWebsiteId,
  website,
  rawWebsite,
  sendMessageToIframe,
  moveFromDraft,
}: UseAgentSessionActionsProps): UseAgentSessionActionsReturn {
  /**
   * Create reverted website data for Firestore update
   */
  const createRevertedWebsiteData = useCallback(
    (changeId: string, change: any) => {
      const sessionData = agentSession.agentSessionData;
      if (!sessionData) return null;

      // Clone the current website data
      const revertedWebsiteData = JSON.parse(
        JSON.stringify(sessionData.websiteData),
      );

      // Handle popup changes
      if (change.entity === 'popup') {
        // Ensure popups array exists
        if (!revertedWebsiteData.popups) {
          revertedWebsiteData.popups = [];
        }

        if (change.type === 'modified') {
          // Get original popup from rawWebsite
          const originalPopup = rawWebsite?.development?.popups?.find(
            (popup: any) => popup.id === change.entityId,
          );

          if (originalPopup) {
            // Replace the modified popup with the original
            const popupIndex = revertedWebsiteData.popups.findIndex(
              (popup: any) => popup.id === change.entityId,
            );
            if (popupIndex !== -1) {
              revertedWebsiteData.popups[popupIndex] = { ...originalPopup };
            }
            console.log('🤖 Reverted modified popup to original:', {
              changeId,
              popupId: change.entityId,
            });
          }
        } else if (change.type === 'created') {
          // Remove the created popup
          revertedWebsiteData.popups = revertedWebsiteData.popups.filter(
            (popup: any) => popup.id !== change.entityId,
          );
          console.log('🤖 Removed created popup:', {
            changeId,
            popupId: change.entityId,
          });
        } else if (change.type === 'deleted') {
          // Re-add the deleted popup from rawWebsite
          const originalPopup = rawWebsite?.development?.popups?.find(
            (popup: any) => popup.id === change.entityId,
          );

          if (originalPopup) {
            revertedWebsiteData.popups.push({ ...originalPopup });
            console.log('🤖 Restored deleted popup:', {
              changeId,
              popupId: change.entityId,
            });
          }
        }

        return revertedWebsiteData;
      }

      // Handle section changes (existing logic)
      if (change.type === 'modified') {
        // Get original section from rawWebsite
        const originalSection = rawWebsite?.development?.sections?.find(
          (section: any) => section.id === change.entityId,
        );

        if (!originalSection) {
          console.warn(
            '🤖 No original section found in rawWebsite for change:',
            changeId,
          );
          return null;
        }

        // Find where this section should be positioned based on original website
        const originalIndex = rawWebsite?.development?.sections?.findIndex(
          (section: any) => section.id === change.entityId,
        );

        if (originalIndex === -1) {
          console.warn(
            '🤖 Could not find original position for section:',
            change.entityId,
          );
          return null;
        }

        // Remove the modified section from its current position
        revertedWebsiteData.sections = revertedWebsiteData.sections.filter(
          (section: any) => section.id !== change.entityId,
        );

        // Calculate the correct insertion position accounting for sections that might not exist
        let insertionIndex = 0;
        if (
          rawWebsite?.development?.sections &&
          originalIndex !== -1 &&
          originalIndex !== undefined
        ) {
          for (let i = 0; i < originalIndex; i++) {
            const originalSectionId = rawWebsite.development.sections[i].id;
            const existsInCurrent = revertedWebsiteData.sections.some(
              (section: any) => section.id === originalSectionId,
            );
            if (existsInCurrent) {
              insertionIndex++;
            }
          }
        }

        // Insert the original section at the calculated position
        revertedWebsiteData.sections.splice(insertionIndex, 0, {
          ...originalSection,
        });

        console.log(
          '🤖 Created reverted website data for modified section at correct position:',
          {
            changeId,
            sectionId: change.entityId,
            originalIndex,
            insertionIndex,
          },
        );
      } else if (change.type === 'created') {
        // Remove the created section
        revertedWebsiteData.sections = revertedWebsiteData.sections.filter(
          (section: any) => section.id !== change.entityId,
        );

        console.log('🤖 Created reverted website data with removed section:', {
          changeId,
          sectionId: change.entityId,
        });
      } else if (change.type === 'deleted') {
        // Re-add the deleted section from rawWebsite
        const originalSection = rawWebsite?.development?.sections?.find(
          (section: any) => section.id === change.entityId,
        );

        if (originalSection) {
          // Find the correct position to insert the section back
          const originalSectionIndex =
            rawWebsite?.development?.sections?.findIndex(
              (section: any) => section.id === change.entityId,
            );

          if (originalSectionIndex !== -1) {
            revertedWebsiteData.sections.splice(originalSectionIndex, 0, {
              ...originalSection,
            });
          } else {
            // Fallback: add at the end
            revertedWebsiteData.sections.push({ ...originalSection });
          }

          console.log(
            '🤖 Created reverted website data with restored section:',
            {
              changeId,
              sectionId: change.entityId,
            },
          );
        } else {
          console.warn(
            '🤖 No original section found to restore for deleted change:',
            changeId,
          );
          return null;
        }
      }

      return revertedWebsiteData;
    },
    [agentSession.agentSessionData, rawWebsite],
  );

  /**
   * Apply a single change to the website
   */
  const applySingleChange = useCallback(
    (changeId: string, change: any) => {
      const sessionData = agentSession.agentSessionData;
      if (!sessionData) return;

      // Handle popup changes
      if (change.entity === 'popup') {
        if (change.type === 'modified' || change.type === 'created') {
          const agentPopup = sessionData.websiteData?.popups?.find(
            (popup: any) => popup.id === change.entityId,
          );

          if (agentPopup) {
            const updateType =
              change.type === 'created' ? 'addPopup' : 'updatePopup';
            const websiteUpdate = websiteUpdater.createWebsiteUpdate(
              dynamicPageId,
              selectedWebsiteId || '',
              change.entityId,
              '',
              agentPopup,
              updateType,
              [],
              website?.development,
            );

            if (websiteUpdate) {
              console.log(
                `🤖 ${change.type === 'created' ? 'Adding' : 'Updating'} agent popup:`,
                {
                  changeId,
                  popupId: change.entityId,
                },
              );
              websiteUpdater.updateWebsiteByEvent(websiteUpdate);
            }
          }
        } else if (change.type === 'deleted') {
          const websiteUpdate = websiteUpdater.createWebsiteUpdate(
            dynamicPageId,
            selectedWebsiteId || '',
            change.entityId,
            '',
            null,
            'deletePopup',
            [],
            website?.development,
          );

          if (websiteUpdate) {
            console.log('🤖 Deleting popup from website:', {
              changeId,
              popupId: change.entityId,
            });
            websiteUpdater.updateWebsiteByEvent(websiteUpdate);
          }
        }

        moveFromDraft();
        return;
      }

      // Handle section changes (existing logic)
      if (change.type === 'modified') {
        const agentSection = sessionData.websiteData?.sections?.find(
          (section: any) => section.id === change.entityId,
        );

        if (agentSection) {
          const websiteUpdate = websiteUpdater.createWebsiteUpdate(
            dynamicPageId,
            selectedWebsiteId || '',
            change.entityId,
            '',
            agentSection,
            'editSection',
            [],
            website?.development,
          );

          if (websiteUpdate) {
            console.log('🤖 Applying agent-modified section content:', {
              changeId,
              sectionId: change.entityId,
            });
            websiteUpdater.updateWebsiteByEvent(websiteUpdate);
          }
        }
      } else if (change.type === 'created') {
        const agentSection = sessionData.websiteData?.sections?.find(
          (section: any) => section.id === change.entityId,
        );

        if (agentSection) {
          const websiteUpdate = websiteUpdater.createWebsiteUpdate(
            dynamicPageId,
            selectedWebsiteId || '',
            '',
            '',
            agentSection,
            'addSectionBelow',
            [],
            website?.development,
          );

          if (websiteUpdate) {
            console.log('🤖 Adding agent-created section to website:', {
              changeId,
              sectionId: change.entityId,
            });
            websiteUpdater.updateWebsiteByEvent(websiteUpdate);
          }
        }
      } else if (change.type === 'deleted') {
        const websiteUpdate = websiteUpdater.createWebsiteUpdate(
          dynamicPageId,
          selectedWebsiteId || '',
          change.entityId,
          '',
          null,
          'deleteSection',
          [],
          website?.development,
        );

        if (websiteUpdate) {
          console.log('🤖 Deleting section from website:', {
            changeId,
            sectionId: change.entityId,
          });
          websiteUpdater.updateWebsiteByEvent(websiteUpdate);
        }
      }

      moveFromDraft();
    },
    [
      agentSession.agentSessionData,
      websiteUpdater,
      dynamicPageId,
      selectedWebsiteId,
      website?.development,
      moveFromDraft,
    ],
  );

  /**
   * Apply new sections order
   */
  const applyNewSectionsOrder = useCallback(() => {
    const websiteData = agentSession.agentSessionData?.websiteData;
    if (!websiteData) return;
    const websiteUpdate = websiteUpdater.createWebsiteUpdate(
      dynamicPageId,
      selectedWebsiteId || '',
      '',
      '',
      websiteData.sections.map((s: any) => ({ id: s.id })),
      'reorderSections',
      undefined,
      website?.development,
    );
    if (websiteUpdate) {
      console.log('🤖 Reordering sections as per agent suggestion');
      websiteUpdater.updateWebsite(websiteUpdate);
    }
  }, [
    agentSession.agentSessionData?.websiteData,
    dynamicPageId,
    selectedWebsiteId,
    websiteUpdater,
    website?.development,
  ]);

  const agentChangeClick = useCallback(
    (changeId: string) => {
      const change = agentSession.agentSessionData?.changes[changeId];
      const sectionId = change?.entityId;
      if (!sectionId) return;
      sendMessageToIframe({
        eventName: 'jumpTo',
        selector: `section[data-section-id="${sectionId}"]`,
      });
    },
    [agentSession.agentSessionData?.changes, sendMessageToIframe],
  );

  const agentAcceptChange = useCallback(
    async (changeId: string) => {
      if (mode !== 'website' || !agentSession.agentSessionData) return;

      const sessionData = agentSession.agentSessionData;
      const change = sessionData.changes[changeId];

      // Apply the change to the website
      applySingleChange(changeId, change);
      applyNewSectionsOrder();

      // Update the change status to accepted
      await agentSession.agentUpdateChanges([
        { id: changeId, status: 'accepted' },
      ]);
    },
    [mode, agentSession, applySingleChange, applyNewSectionsOrder],
  );

  const agentDiscardChange = useCallback(
    async (changeId: string) => {
      if (mode !== 'website' || !agentSession.agentSessionData) return;

      const sessionData = agentSession.agentSessionData;
      const change = sessionData.changes[changeId];

      if (change) {
        console.log(
          '🤖 Discarding agent change and reverting to original state:',
          changeId,
        );

        // Create reverted website data
        const revertedWebsiteData = createRevertedWebsiteData(changeId, change);

        if (revertedWebsiteData) {
          // Update Firestore with reverted website data
          await agentSession.agentUpdateWebsiteData(revertedWebsiteData);
        }
      }

      // Update the change status to rejected
      await agentSession.agentUpdateChanges([
        { id: changeId, status: 'rejected' },
      ]);
    },
    [mode, agentSession, createRevertedWebsiteData],
  );

  const agentAcceptAllChanges = useCallback(async () => {
    if (mode !== 'website' || !agentSession.agentSessionData) return;

    const sessionData = agentSession.agentSessionData;
    const changeUpdates: { id: string; status: 'accepted' }[] = [];

    // Process changes in specific order: delete, create, modify
    const changeOrder = ['deleted', 'created', 'modified'] as const;

    for (const changeType of changeOrder) {
      for (const [changeId, change] of Object.entries(sessionData.changes)) {
        if (change.status === 'pending' && change.type === changeType) {
          applySingleChange(changeId, change);
          changeUpdates.push({ id: changeId, status: 'accepted' });
        }
      }
    }

    applyNewSectionsOrder();

    // Update all changes to accepted status
    if (changeUpdates.length > 0) {
      await agentSession.agentUpdateChanges(changeUpdates);
    }
  }, [mode, agentSession, applyNewSectionsOrder, applySingleChange]);

  const agentDiscardAllChanges = useCallback(async () => {
    if (mode !== 'website' || !agentSession.agentSessionData) return;

    const sessionData = agentSession.agentSessionData;
    const changeUpdates: { id: string; status: 'rejected' }[] = [];

    console.log(
      '🤖 Discarding all agent changes and reverting to original website state',
    );

    // For discarding all changes, we revert to the original rawWebsite state
    if (rawWebsite?.development) {
      const originalWebsiteData = JSON.parse(
        JSON.stringify(rawWebsite.development),
      );

      // Update Firestore with original website data
      await agentSession.agentUpdateWebsiteData(originalWebsiteData);
    }

    // Collect all pending changes for rejection
    for (const [changeId, change] of Object.entries(sessionData.changes)) {
      if (change.status === 'pending') {
        changeUpdates.push({ id: changeId, status: 'rejected' });
      }
    }

    // Update all changes to rejected status
    if (changeUpdates.length > 0) {
      await agentSession.agentUpdateChanges(changeUpdates);
    }
  }, [mode, agentSession, rawWebsite]);

  return {
    agentAcceptChange,
    agentAcceptAllChanges,
    agentDiscardChange,
    agentDiscardAllChanges,
    agentChangeClick,
  };
}
